import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { ccApi, errMsg, fmtTs } from "./ccApi";
import { ActionBtn, AuditRow, Empty, Pill, Section } from "./ccUi";
import ReasonDialog from "./ReasonDialog";

const NEXT = { open: ["under_review", "actioned"], under_review: ["actioned", "open"], actioned: ["appealed"], appealed: ["upheld", "overturned"], upheld: [], overturned: [] };
const TONE = { open: "zinc", under_review: "blue", actioned: "amber", appealed: "red", upheld: "green", overturned: "green" };

export default function CasesTab({ setTarget, me }) {
  const [filter, setFilter] = useState("");
  const [cases, setCases] = useState([]);
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [dlg, setDlg] = useState(null);

  const refresh = useCallback(() => ccApi.cases(filter ? { status: filter } : {}).then((d) => setCases(d.cases)).catch(() => {}), [filter]);
  useEffect(() => { refresh(); }, [refresh]);
  const open = (id) => ccApi.case(id).then(setSel).catch((e) => toast.error(errMsg(e)));

  const addNote = async () => {
    try { await ccApi.caseNote(sel.id, note); setNote(""); toast.success("Note added"); open(sel.id); } catch (e) { toast.error(errMsg(e)); }
  };
  const submit = async ({ reason }) => {
    try { await ccApi.caseStatus(sel.id, dlg.status, reason); toast.success(`Case → ${dlg.status}`); setDlg(null); open(sel.id); refresh(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (sel) {
    return (
      <div className="space-y-3" data-testid="cc-case-detail">
        <button type="button" data-testid="cc-case-back" onClick={() => setSel(null)} className="text-[11px] text-zinc-500 hover:text-white">← all cases</button>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-white font-semibold">Case {sel.id.slice(0, 8)}</span>
          <Pill tone={TONE[sel.status]} testid="cc-case-status">{sel.status.replace("_", " ")}</Pill>
          <Pill>{sel.category}</Pill>
          <span className="text-xs text-zinc-500">{sel.target_type} · <button type="button" className="hover:underline" onClick={() => setTarget({ type: sel.target_type === "user" ? "user" : sel.target_type, id: sel.target_id, _tab: sel.target_type === "user" ? "member" : sel.target_type === "live" ? "live" : "content" })}>{sel.target_id.slice(0, 8)}…</button></span>
        </div>
        <div className="text-xs text-zinc-400">Opened {fmtTs(sel.created_at)} · updated {fmtTs(sel.updated_at)}{sel.appealed_at && ` · appealed ${fmtTs(sel.appealed_at)}`}{sel.resolved_at && ` · resolved ${fmtTs(sel.resolved_at)} (${sel.resolution})`}</div>
        <div className="text-sm text-zinc-200 italic">“{sel.reason}”</div>
        {NEXT[sel.status].length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="cc-case-transitions">
            {NEXT[sel.status].map((s) => <ActionBtn key={s} tone={s === "overturned" || s === "upheld" ? "good" : s === "appealed" ? "warn" : "default"} testid={`cc-case-to-${s}`} onClick={() => setDlg({ title: `Move case → ${s.replace("_", " ")}`, cta: "Update", status: s })}>→ {s.replace("_", " ")}</ActionBtn>)}
          </div>
        )}
        <Section title={`Timeline · ${sel.timeline.length}`} testid="cc-case-timeline">
          {sel.timeline.map((t, i) => <div key={i} className="text-xs text-zinc-300"><span className="text-zinc-500">{fmtTs(t.ts)}</span> · <span className="font-mono text-white">{t.action}</span> by @{t.actor_handle} <span className="text-zinc-500">({t.actor_role})</span> — “{t.reason}”</div>)}
        </Section>
        <Section title={`Internal notes · ${sel.notes.length}`} testid="cc-case-notes">
          {sel.notes.map((n) => <div key={n.id} className="text-xs text-zinc-300"><span className="text-zinc-500">{fmtTs(n.ts)}</span> @{n.actor_handle}: {n.text}</div>)}
          <div className="flex gap-2 items-end">
            <Textarea data-testid="cc-case-note-input" rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (never shown to members)" className="bg-black/40 border-white/10 text-white text-xs" />
            <ActionBtn testid="cc-case-note-submit" onClick={addNote} disabled={!note.trim()}>Add</ActionBtn>
          </div>
        </Section>
        {(sel.evidence || sel.evidence_count != null) && (
          <Section title={`Evidence · ${sel.evidence ? sel.evidence.length : sel.evidence_count}`} testid="cc-case-evidence">
            {!sel.evidence && <Empty>Evidence items are restricted to Safety Admins and the owner.</Empty>}
            {sel.evidence?.map((e) => (
              <div key={e.id} className="text-xs text-zinc-300 flex flex-wrap items-center gap-2">
                <Pill tone={e.status === "ready" ? "green" : e.status === "recording" ? "blue" : "amber"}>{e.capture_type} · {e.status}</Pill>
                <span>{fmtTs(e.ts)} · {e.reason_code} · @{e.moderator_handle}</span>
                <span className="text-zinc-500">retain until {fmtTs(e.retention_until)}</span>
                {e.capture_type === "screenshot" && e.status === "ready" && <a data-testid={`cc-evidence-open-${e.id}`} href={ccApi.evidenceFileUrl(e.id)} target="_blank" rel="noreferrer" className="text-sky-300 hover:underline">view (logged)</a>}
                {e.unavailable_reason && <span className="text-amber-300">{e.unavailable_reason}</span>}
              </div>
            ))}
          </Section>
        )}
        <Section title={`Audit · ${sel.audit.length}`} testid="cc-case-audit">
          {sel.audit.map((r) => <AuditRow key={r.id} row={r} />)}
        </Section>
        <ReasonDialog spec={dlg} onClose={() => setDlg(null)} onSubmit={submit} />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1" data-testid="cc-case-filters">
        {["", "open", "under_review", "actioned", "appealed", "upheld", "overturned"].map((s) => <button key={s || "all"} type="button" data-testid={`cc-case-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`px-2 py-0.5 text-[11px] rounded-full ${filter === s ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>{s ? s.replace("_", " ") : "all"}</button>)}
      </div>
      <Section title={`Moderation cases · ${cases.length}`} testid="cc-cases">
        {cases.length === 0 && <Empty>No cases.</Empty>}
        {cases.map((c) => (
          <button key={c.id} type="button" data-testid={`cc-case-row-${c.id}`} onClick={() => open(c.id)} className="w-full text-left text-xs px-2 py-2 rounded hover:bg-white/5 flex flex-wrap items-center gap-2">
            <Pill tone={TONE[c.status]}>{c.status.replace("_", " ")}</Pill>
            <span className="text-white">{c.category}</span>
            <span className="text-zinc-400 truncate flex-1">“{c.reason}”</span>
            <span className="text-zinc-500">{c.target_type} · {fmtTs(c.updated_at)}</span>
          </button>
        ))}
      </Section>
    </div>
  );
}
